import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ShieldCheck, Ban, Undo2 } from 'lucide-react';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { hitlActionErrorToast } from '../../../utils/hitlActionToast.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';

function getApproveAccessLabel({ isResolving, hasGate, nextKeyNumber, requiredCount, t }) {
  if (isResolving) return t('hitl.resolving');
  if (!hasGate) return t('breakGlass.approveAccessNoGate');
  return t('breakGlass.approveAccess', { done: nextKeyNumber, total: requiredCount });
}

/**
 * Break Glass decision: Approve / Deny confirm in ModalLayout (consistent with
 * Continuation / Question). Reverse deny is the delivery-call addition when one
 * key denied after another approved. Approver rows live in BreakGlassBody via
 * EventDraft.
 *
 * CT-5096: the M-of-N key gate is local UI state; the HITL interrupt resolves only
 * at the TERMINAL moment — the last key granting → POST /v1/tasks/hitl/resolve
 * `{ status: approved }`; a hard deny with no prior confirmed key (not reversible)
 * → `{ status: rejected }`. Per-key approve/deny/reverse stay local.
 */
function BreakGlassDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const detail = event.detail ?? {};
  const requiredCount = detail.requiredCount ?? 2;
  const { breakGlassApprovers, setBreakGlassApprovers } = useEventDraft();
  const { resolveHitlEvent, pendingDecisionIds } = useWorkQueueContext();
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;

  const [mode, setMode] = useState(null); // 'approve' | 'deny' | null

  // Seed the shared gate once from the event detail (provider is keyed per event).
  useEffect(() => {
    if (breakGlassApprovers == null && detail.approvers) {
      setBreakGlassApprovers(detail.approvers);
    }
  }, [breakGlassApprovers, detail.approvers, setBreakGlassApprovers]);

  const approvers = breakGlassApprovers ?? detail.approvers ?? [];
  const hasGate = approvers.length > 0;
  const confirmedCount = approvers.filter((a) => a.status === 'confirmed').length;
  const deniedApprover = approvers.find((a) => a.status === 'denied');
  const granted = confirmedCount >= requiredCount;
  const nextPending = approvers.find((a) => a.status === 'pending');
  const nextKeyNumber = Math.min(confirmedCount + 1, requiredCount);
  // Meeting scope: reverse only when a deny follows at least one confirmed key.
  const canReverseDeny = Boolean(deniedApprover) && confirmedCount > 0;

  const setStatus = useCallback(
    (id, status, extra = {}) => {
      setBreakGlassApprovers((prev) => {
        const base = prev ?? detail.approvers ?? [];
        return base.map((a) => (a.id === id ? { ...a, status, ...extra } : a));
      });
    },
    [detail.approvers, setBreakGlassApprovers]
  );

  const handleCloseMode = useCallback(() => setMode(null), []);

  const handleConfirmApprove = useCallback(async () => {
    if (!nextPending) return;
    const confirmKey = () =>
      setStatus(nextPending.id, 'confirmed', { confirmedAt: t('breakGlass.justNow') });

    // Not the final key yet → local gate progress only.
    if (confirmedCount + 1 < requiredCount) {
      confirmKey();
      toast.success({
        title: t('breakGlass.approvedTitle'),
        message: t('breakGlass.approvedMessage'),
      });
      return;
    }
    // Final key grants access → resolve the interrupt FIRST, and flip the gate only
    // once it succeeds. Confirming up front would leave the gate `granted` on a failed
    // resolve — Approve/Deny hidden and no `nextPending` left to retry with — while the
    // interrupt is still pending and the card is still in the inbox.
    try {
      await resolveHitlEvent(event.id, {
        status: 'approved',
        auditAction: t('breakGlass.auditGranted'),
      });
    } catch (err) {
      if (isDecisionSkipped(err)) return;
      const { title, message } = hitlActionErrorToast(t, err, 'breakGlass');
      toast.error({ title, message });
      throw err;
    }
    confirmKey();
    toast.success({
      title: t('breakGlass.grantedTitle'),
      message: t('breakGlass.grantedMessage'),
    });
  }, [nextPending, setStatus, confirmedCount, requiredCount, resolveHitlEvent, event.id, t, toast]);

  const handleConfirmDeny = useCallback(
    ({ reason }) => {
      if (!nextPending || !reason?.trim()) return;
      // Deny is per-key gate state (reversible in the meeting scope), NOT a terminal
      // decision — the prototype keeps Approve/Deny open after a deny. The HITL
      // interrupt resolves only when the gate GRANTS (all keys → approved); there is
      // no terminal "reject" button here, so deny never calls hitl/resolve.
      setStatus(nextPending.id, 'denied');
      toast.success({ title: t('breakGlass.deniedTitle'), message: t('breakGlass.deniedMessage') });
    },
    [nextPending, setStatus, t, toast]
  );

  const handleReverseDeny = useCallback(() => {
    if (!deniedApprover) return;
    setStatus(deniedApprover.id, 'pending');
    toast.success({
      title: t('breakGlass.reversedTitle'),
      message: t('breakGlass.reversedMessage'),
    });
  }, [deniedApprover, setStatus, t, toast]);

  const primaryActions = useMemo(() => {
    if (granted) return [];
    if (canReverseDeny) {
      return [
        {
          id: 'reverse',
          label: t('breakGlass.reverseDeny'),
          icon: Undo2,
          variant: 'secondary',
          size: 'md',
          disabled: isResolving,
          onClick: handleReverseDeny,
        },
      ];
    }
    return [
      {
        id: 'approve',
        label: getApproveAccessLabel({
          isResolving,
          hasGate,
          nextKeyNumber,
          requiredCount,
          t,
        }),
        icon: ShieldCheck,
        size: 'md',
        disabled: !hasGate || !nextPending || isResolving,
        onClick: () => setMode('approve'),
      },
      {
        id: 'deny',
        label: t('breakGlass.deny'),
        icon: Ban,
        variant: 'danger',
        size: 'md',
        disabled: !hasGate || !nextPending || isResolving,
        onClick: () => setMode('deny'),
      },
    ];
  }, [
    granted,
    canReverseDeny,
    isResolving,
    hasGate,
    t,
    handleReverseDeny,
    nextKeyNumber,
    requiredCount,
    nextPending,
  ]);

  const approveRows = useMemo(
    () => [
      { label: t('breakGlass.requester'), value: detail.requester?.name ?? '—' },
      { label: t('breakGlass.partition'), value: detail.partition ?? '—' },
      { label: t('breakGlass.duration'), value: detail.duration ?? '—' },
    ],
    [t, detail.requester?.name, detail.partition, detail.duration]
  );

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <DecisionConfirmModal
        isOpen={mode === 'approve'}
        onClose={handleCloseMode}
        icon={<ShieldCheck size={20} className="text-sky-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('breakGlass.approveTitle')}
        subtitle={t('breakGlass.approveSummary', {
          requester: detail.requester?.name ?? '',
          duration: detail.duration ?? '',
          partition: detail.partition ?? '',
        })}
        summaryRows={approveRows}
        confirmLabel={t('breakGlass.confirmApproval')}
        onConfirm={handleConfirmApprove}
      />

      <DecisionConfirmModal
        key={mode === 'deny' ? `deny-${event.id}` : 'deny-closed'}
        isOpen={mode === 'deny'}
        onClose={handleCloseMode}
        icon={<Ban size={20} className="text-rose-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('breakGlass.denyTitle')}
        subtitle={t('breakGlass.denyPrompt')}
        requireReason
        reasonLabel={t('breakGlass.denyReasonLabel')}
        reasonPlaceholder={t('breakGlass.denyReasonPlaceholder')}
        confirmLabel={t('breakGlass.confirmDeny')}
        confirmVariant="danger"
        onConfirm={handleConfirmDeny}
      />
    </>
  );
}

export default BreakGlassDecision;
